import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    ArrowLeft,
    Edit,
    Trash2,
    Car,
    Home,
    MapPin,
    DollarSign,
    Calendar,
    CheckCircle,
    Clock,
    XCircle,
    Tag,
    Image as ImageIcon,
} from 'lucide-react';
import {
    useGetDealerListingQuery,
    useDeleteDealerListingMutation,
    useMarkDealerListingSoldMutation,
} from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';

const DealerViewListing: React.FC = () => {
    const navigate = useNavigate();
    const { listing_id } = useParams<{ listing_id: string }>();
    const listingId = listing_id ? parseInt(listing_id) : null;
    const { token } = useSelector((state: RootState) => state.auth);

    const { data: listingResponse, isLoading, error, refetch } = useGetDealerListingQuery(listingId!, {
        skip: !listingId || !token,
    });
    const [deleteListing, { isLoading: isDeleting }] = useDeleteDealerListingMutation();
    const [markSold, { isLoading: isMarkingSold }] = useMarkDealerListingSoldMutation();

    const listing = listingResponse?.data;

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this listing?')) {
            return;
        }

        try {
            await deleteListing(listingId!).unwrap();
            toast.success('Listing deleted successfully');
            navigate('/dealer/listings');
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to delete listing');
        }
    };

    const handleMarkSold = async () => {
        if (!window.confirm('Mark this listing as sold? This action cannot be undone.')) {
            return;
        }

        try {
            await markSold(listingId!).unwrap();
            toast.success('Listing marked as sold successfully');
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to mark listing as sold');
        }
    };

    const getApprovalStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return <Badge variant="success">Approved</Badge>;
            case 'pending':
                return <Badge variant="warning">Pending Approval</Badge>;
            case 'rejected':
                return <Badge variant="danger">Rejected</Badge>;
            default:
                return null;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !listing) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Failed to load listing</div>
            </div>
        );
    }

    const primaryImage = listing.images?.find(img => img.is_primary) || listing.images?.[0];
    const otherImages = listing.images?.filter(img => img.image_id !== primaryImage?.image_id) || [];

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/dealer/listings')}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{listing.title}</h1>
                        <div className="flex items-center gap-2 mt-2">
                            {getApprovalStatusBadge(listing.approval_status)}
                            {listing.status === 'sold' && (
                                <Badge className="bg-purple-100 text-purple-800">Sold</Badge>
                            )}
                            {listing.status === 'active' && (
                                <Badge variant="success">Active</Badge>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    {listing.approval_status === 'approved' && listing.status !== 'sold' && (
                        <Button
                            onClick={handleMarkSold}
                            disabled={isMarkingSold}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                            {isMarkingSold ? 'Marking...' : 'Mark as Sold'}
                        </Button>
                    )}
                    <Button
                        onClick={() => navigate(`/dealer/listings/${listingId}/edit`)}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Edit className="w-4 h-4" />
                        Edit
                    </Button>
                    <Button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50 flex items-center gap-2"
                    >
                        <Trash2 className="w-4 h-4" />
                        Delete
                    </Button>
                </div>
            </div>

            {/* Rejection Reason */}
            {listing.approval_status === 'rejected' && listing.rejection_reason && (
                <Card className="p-4 bg-red-50 border-red-200">
                    <div className="flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                            <h3 className="text-sm font-semibold text-red-900 mb-1">Listing Rejected</h3>
                            <p className="text-sm text-red-700">{listing.rejection_reason}</p>
                        </div>
                    </div>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Images */}
                    <Card className="p-6">
                        <div className="space-y-4">
                            {primaryImage && (
                                <div className="relative">
                                    <img
                                        src={primaryImage.image_path}
                                        alt={listing.title}
                                        className="w-full h-96 object-cover rounded-lg"
                                    />
                                </div>
                            )}
                            {otherImages.length > 0 && (
                                <div className="grid grid-cols-4 gap-2">
                                    {otherImages.map((image) => (
                                        <img
                                            key={image.image_id}
                                            src={image.image_path}
                                            alt={listing.title}
                                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                        />
                                    ))}
                                </div>
                            )}
                            {listing.images?.length === 0 && (
                                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <ImageIcon className="w-16 h-16 text-gray-400" />
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Description */}
                    <Card className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                        <p className="text-gray-700 whitespace-pre-wrap">{listing.description}</p>
                    </Card>

                    {/* Details */}
                    <Card className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            {listing.listing_type === 'car' ? 'Car' : 'House'} Details
                        </h2>
                        {listing.listing_type === 'car' && listing.car_details && (
                            <div className="grid grid-cols-2 gap-4">
                                {listing.car_details.vin && (
                                    <div>
                                        <p className="text-sm text-gray-600">VIN Number</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.car_details.vin}</p>
                                    </div>
                                )}
                                {listing.car_details.make && (
                                    <div>
                                        <p className="text-sm text-gray-600">Make</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.car_details.make}</p>
                                    </div>
                                )}
                                {listing.car_details.model && (
                                    <div>
                                        <p className="text-sm text-gray-600">Model</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.car_details.model}</p>
                                    </div>
                                )}
                                {listing.car_details.year && (
                                    <div>
                                        <p className="text-sm text-gray-600">Year</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.car_details.year}</p>
                                    </div>
                                )}
                                {listing.car_details.mileage && (
                                    <div>
                                        <p className="text-sm text-gray-600">Mileage</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.car_details.mileage.toLocaleString()} miles</p>
                                    </div>
                                )}
                            </div>
                        )}
                        {listing.listing_type === 'house' && listing.house_details && (
                            <div className="grid grid-cols-2 gap-4">
                                {listing.house_details.bedrooms && (
                                    <div>
                                        <p className="text-sm text-gray-600">Bedrooms</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.house_details.bedrooms}</p>
                                    </div>
                                )}
                                {listing.house_details.bathrooms && (
                                    <div>
                                        <p className="text-sm text-gray-600">Bathrooms</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.house_details.bathrooms}</p>
                                    </div>
                                )}
                                {listing.house_details.area && (
                                    <div>
                                        <p className="text-sm text-gray-600">Area</p>
                                        <p className="text-lg font-semibold text-gray-900">{listing.house_details.area} sq ft</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Price Card */}
                    <Card className="p-6">
                        <div className="text-center">
                            <p className="text-sm text-gray-600 mb-2">Price</p>
                            <p className="text-4xl font-bold text-gray-900 mb-4">
                                {listing.currency_symbol || '$'}{listing.price.toLocaleString()}
                            </p>
                            {listing.location && (
                                <div className="flex items-center justify-center gap-2 text-gray-600">
                                    <MapPin className="w-4 h-4" />
                                    <span className="text-sm">{listing.location}</span>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Tags */}
                    {listing.tags && listing.tags.length > 0 && (
                        <Card className="p-6">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Tag className="w-4 h-4" />
                                Tags
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {listing.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Info */}
                    <Card className="p-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Listing Information</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Listing ID</span>
                                <span className="font-medium text-gray-900">#{listing.listing_id}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Type</span>
                                <span className="font-medium text-gray-900 capitalize">{listing.listing_type}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Status</span>
                                <span className="font-medium text-gray-900 capitalize">{listing.status}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Approval</span>
                                {getApprovalStatusBadge(listing.approval_status)}
                            </div>
                            {listing.created_at && (
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Created</span>
                                    <span className="font-medium text-gray-900">
                                        {formatDateUtil(listing.created_at)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DealerViewListing;
